import { BitReader } from '../aac/bitReader';
import type { CaptionDataTriplet, CaptionDataType } from '../types/captionDataTriplet';

export function parseSeiForClosedCaptions(rbsp: Uint8Array, captionDataTriplets: CaptionDataTriplet[], propagateFailures = false) {
  const bitReader = new BitReader(rbsp);

  try {
    do {
      sei_message(bitReader, captionDataTriplets);
    } while (more_rbsp_data(bitReader));
  } catch (err) {
    if (propagateFailures) {
      throw err;
    } else {
      // We don't want to make SEI errors block the whole segment ingest
      console.trace(`Failed to process sei rbsp: ${JSON.stringify([...rbsp])}`, err);
    }
  }
}


function sei_message(bitReader: BitReader, captionDataTriplets: CaptionDataTriplet[]) {
  let payloadType = 0;
  let tmp: number;
  do {
    tmp = bitReader.readBits(8);
    payloadType += tmp;
  } while (tmp === 0xFF);

  let payloadSize = 0;
  do {
    tmp = bitReader.readBits(8);
    payloadSize += tmp;
  } while (tmp === 0xFF);

  sei_payload(bitReader, payloadType, payloadSize, captionDataTriplets);
}


function sei_payload(bitReader: BitReader, payloadType: number, payloadSize: number, captionDataTriplets: CaptionDataTriplet[]) {
  if (payloadType === 4) {
    bitReader.markPosition();
    const processed = user_data_registered_itu_t_t35(bitReader, payloadSize, captionDataTriplets);
    if (processed) {
      return;
    }
    // If we have not processed the full payload, let's rewind the position
    // to skip all the payload bytes
    bitReader.restoreMark();
  }

  // The payload was not a closed caption one that we support, let's skip
  for (let i = 0; i < payloadSize; i++) {
    bitReader.readBits(8);
  }

  bitReader.alignByte();
}


/**
 * Returns true if we have processed the payload entirely.
 */
function user_data_registered_itu_t_t35(bitReader: BitReader, payloadSize: number, captionDataTriplets: CaptionDataTriplet[]): boolean {
  const itu_t_t35_country_code = bitReader.readBits(8);

  if (itu_t_t35_country_code !== 0xB5) {
    // Closed captions must have 0xB5 as the itu_t_t35_country_code
    return false;
  }

  if (bitReader.readBits(16) !== 0x0031) {
    // Closed captions must have the ATSC provider code
    return false;
  }

  if (bitReader.readBits(32) !== 0x47413934) {
    // Closed captions must have the ATSC1 user identifier code
    return false;
  }

  if (bitReader.readBits(8) !== 0x03) {
    // Closed captions must have the cc_data user data type code
    return false;
  }

  const reserved = bitReader.readBits(1);
  if (reserved !== 1) {
    return false;
  }

  const process_cc_data_flag = bitReader.readBits(1);

  // This flag is for skipping trailing data, but we already
  // have the full buffer, so we can just ignore everything that comes after
  // the data triplets
  /*const additional_data_flag =*/ bitReader.readBits(1);
  const cc_count = bitReader.readBits(5);

  // reserved
  bitReader.readBits(8);
  payloadSize -= 10;

  for (let i = 0; i < cc_count; i++) {
    /*const marker_bits =*/ bitReader.readBits(5);

    const cc_valid = bitReader.readBits(1);
    const cc_type = bitReader.readBits(2);
    const cc_data_1 = bitReader.readBits(8);
    const cc_data_2 = bitReader.readBits(8);

    payloadSize -= 3;

    if (process_cc_data_flag === 1) {
      captionDataTriplets.push({
        valid: cc_valid === 1,
        type: cc_type as CaptionDataType,
        data1: cc_data_1,
        data2: cc_data_2,
      });
    }
  }

  while (payloadSize > 0) {
    payloadSize--;
    bitReader.readBits(8);
  }

  return true;
}



function more_rbsp_data(bitReader: BitReader): boolean {
  const availableBits = bitReader.getAvailableBits();
  if (availableBits <= 16) {
    return false;
  }

  const maybeTrailingBits = bitReader.peekNextBits(16);
  if (maybeTrailingBits === null) {
    return false;
  }
  // eslint-disable-next-line no-bitwise
  if (maybeTrailingBits === (1 << (availableBits - 1))) {
    // The trailing bit pattern is 1 followed by 0s,
    // so if we have found it, then there is no more data
    return false;
  }

  return true;
}
